// src/pages/CourseTime/hooks/useSlideContentRenderer.js
import React from 'react';
import { renderPlainTextWithFormatting, markdownInlineToHtml } from '../utils';

// Shared rich-text / TTS rendering helpers used by every slide layout.
// These used to live as closures inside SlideRenderer.comp.jsx.
export const useSlideContentRenderer = ({ renderSpokenText, skipTitleSpeech }) => {
  // Strip HTML tags for TTS - convert to plain text with spaces
  const stripHtmlForTTS = (text) => {
    if (typeof text !== 'string') return String(text || '');
    return text
        // Preserve boundaries between block/list tags so words don't glue together when tags are stripped:
        // "</li><li>" -> "</li> <li>"
        .replace(/<\/li>\s*<li/gi, '</li> <li')
        .replace(/<\/p>\s*<p/gi, '</p> <p')
        .replace(/<\/(dt|dd)>\s*<(dt|dd)/gi, '</$1> <$2')
        // Keep punctuation tokenization consistent with DOM parsing:
        // "</b>." -> "</b> ." so "." doesn't get attached to the last bold word
        .replace(/(<\/[^>]+>)([.,!?;:])/g, '$1 $2')
        .replace(/<br\s*\/?>/gi, ' ')          // Replace <br> with space
        .replace(/<[^>]+>/g, '')               // Remove all HTML tags
        .replace(/&nbsp;/gi, ' ')              // Replace &nbsp; with space
        .replace(/&amp;/gi, '&')               // Replace &amp; with &
        .replace(/&lt;/gi, '<')                // Replace &lt; with <
        .replace(/&gt;/gi, '>')                // Replace &gt; with >
        .replace(/&quot;/gi, '"')              // Replace &quot; with "
        .replace(/&#39;/gi, "'")                // Replace &#39; with '
        .replace(/\s+/g, ' ')                  // Collapse multiple spaces
        .trim();
  };

  const fixHtml = (html) => {
    if (!html) return "";

    return html
        // Fix self-closing <li/>
        .replace(/<li\s*\/\s*>/gi, "</li>")

        // Fix self-closing <dt/> and <dd/>
        .replace(/<dt\s*\/\s*>/gi, "</dt>")
        .replace(/<dd\s*\/\s*>/gi, "</dd>")

        // Fix self-closing <ul/> and variations
        .replace(/<ul\s*\/\s*>/gi, "</ul>")

        // Fix self-closing <dl/>
        .replace(/<dl\s*\/\s*>/gi, "</dl>")

        // Fix self-closing <ol/>
        .replace(/<ol\s*\/\s*>/gi, "</ol>")

        // Fix nested <li><p>…</p><li/> → wrap correctly
        .replace(/<\/p>\s*<\/li>/gi, "</p></li>")

        // Fix nested <dt><p>…</p><dt/> and <dd><p>…</p><dd/>
        .replace(/<\/p>\s*<\/dt>/gi, "</p></dt>")
        .replace(/<\/p>\s*<\/dd>/gi, "</p></dd>")

        // Remove accidental broken <li></li></li>
        .replace(/<\/li>\s*<\/li>/gi, "</li>")

        // Remove accidental broken <dt></dt></dt> and <dd></dd></dd>
        .replace(/<\/dt>\s*<\/dt>/gi, "</dt>")
        .replace(/<\/dd>\s*<\/dd>/gi, "</dd>")
        // Avoid double full stop after bold/italic etc. (e.g. ".</b>." -> ".</b>")
        .replace(/(\.<\/[^>]+>)\s*\.\s*$/i, '$1')
        // Also collapse any duplicate trailing full stops (e.g. ".." or ". ." at end)
        .replace(/\.(\s*\.)+\s*$/, '.')
        // Fix missing spaces after sentence-ending punctuation in generated content:
        .replace(/([.!?])((?:<\/[^>]+>\s*)+)([A-ZÁÉÍÓÚÑÜ])/g, '$1$2 $3')
        .replace(/([.!?])([A-ZÁÉÍÓÚÑÜ])/g, '$1 $2');
  };

  const renderHtmlWithSpeech = (key, html) => {
    const fixedHtml = fixHtml(String(html).trim());
    const ttsText = stripHtmlForTTS(fixedHtml);

    if (!renderSpokenText) {
      return <span dangerouslySetInnerHTML={{ __html: fixedHtml }} />;
    }

    const highlightedSpans = Array.isArray(renderSpokenText(key, ttsText))
        ? renderSpokenText(key, ttsText)
        : [renderSpokenText(key, ttsText)];

    const wordIndex = { current: 0 };

    const nodeToReact = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const words = node.textContent?.match(/\S+/g) || [];
        if (!words.length) return node.textContent; // keep whitespace

        const start = wordIndex.current;
        const end = start + words.length;
        const spans = highlightedSpans.slice(start, end);
        wordIndex.current = end;

        return spans.length ? <>{spans}</> : node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes).map(nodeToReact);

        const props = {
          key: node.tagName + Math.random(),
          ...Object.fromEntries(Array.from(node.attributes).map(a => [a.name, a.value])),
          style: node.style.cssText ? Object.fromEntries([...node.style].map(p => [p, node.style.getPropertyValue(p)])) : undefined
        };

        return React.createElement(node.tagName.toLowerCase(), props, children);
      }

      return null;
    };

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = fixedHtml;
    const elements = Array.from(tempDiv.childNodes).map(nodeToReact);

    return elements.length === 1 ? elements[0] : <>{elements}</>;
  };

  const renderContent = (key, content) => {
    if (!content) return null;

    const text = fixHtml(String(content).trim());
    const hasHtml = /<[^>]+>/.test(text);

    if (hasHtml) return renderHtmlWithSpeech(key, text);
    if (!renderSpokenText) return renderPlainTextWithFormatting(text);

    // Plain text with markdown (**bold**, _italic_) has no real tags for the
    // TTS word-highlighter to preserve on its own, so convert the markers to
    // <strong>/<em> first and route through the same tag-preserving path
    // used for real HTML — otherwise the markers print literally.
    const withInlineTags = markdownInlineToHtml(text);
    return /<[^>]+>/.test(withInlineTags)
        ? renderHtmlWithSpeech(key, withInlineTags)
        : renderSpokenText(key, stripHtmlForTTS(text));
  };

  return { renderContent, renderHtmlWithSpeech, fixHtml, stripHtmlForTTS };
};
